/**
 * Wire types for the Hermes API, mirroring docs/API.md.
 *
 * Timestamps arrive as ISO-8601 strings (the Worker converts from epoch-ms).
 * Fields the contract does not pin down exactly are typed optional so a slightly
 * richer server response never breaks rendering.
 */

export type MediaType = 'movie' | 'tv'

export type RequestStage =
  | 'pending'
  | 'approved'
  | 'declined'
  | 'downloading'
  | 'available'
  | 'failed'

/** Keep in sync with NOTIFICATION_EVENTS in src/db/schema.ts. */
export const NOTIFICATION_EVENTS = [
  'request.approved',
  'request.declined',
  'request.downloading',
  'request.available',
  'request.failed',
  'account.expiring',
  'account.disabled',
  'invite.redeemed',
  'announcement',
] as const

export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number]

export interface ListResponse<T> {
  items: T[]
  total: number
  /** Present on lists that can be served from the D1 snapshot. */
  stale?: boolean
}

// ---------------------------------------------------------------------------
// Auth + account
// ---------------------------------------------------------------------------

export interface User {
  id: string
  jellyfinUserId: string
  username: string
  /** `pending` means signed in but not yet able to watch. */
  approvalState?: 'approved' | 'pending' | 'rejected'
  approvedAt?: string | null
  email: string | null
  isAdmin: boolean
  disabled: boolean
  profileId: string | null
  expiresAt: string | null
  label?: string | null
  notes?: string | null
  lastLoginAt?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface Features {
  seerr: boolean
  radarr: boolean
  sonarr: boolean
  qbittorrent: boolean
  push: boolean
}

/** Where the other services live. Sent by /api/auth/me. */
export interface ServerLinks {
  name: string
  jellyfinUrl: string
  seerrUrl: string | null
}

export interface MeResponse {
  user: User
  features: Features
  server?: ServerLinks
}

export interface LoginResponse {
  user: User
}

export interface SessionSummary {
  id: string
  userAgent: string | null
  ip: string | null
  createdAt: string
  lastSeenAt: string | null
  expiresAt: string
  /** True for the session backing this browser's cookie. */
  current?: boolean
}

export interface AccountResponse {
  user: User
  profile?: { id: string; name: string; description?: string | null } | null
  expiresAt?: string | null
}


export interface ReferralResponse {
  code: string
  /** Absolute URL, built from the Worker's PUBLIC_URL. */
  url: string
  usedCount?: number
  maxUses?: number | null
  /** Always true now: referral signups are held for admin approval. */
  needsApproval?: boolean
}

// ---------------------------------------------------------------------------
// Requests + downloads
// ---------------------------------------------------------------------------

export interface DownloadDetail {
  progress: number
  eta: number | null
  size: number
  sizeLeft: number
  status: string
  client?: string | null
  /** Bytes per second, from qBittorrent. Null when it is unreachable. */
  speed?: number | null
  /** Connected seeds, from qBittorrent. */
  seeds?: number | null
}

export interface MediaRequest {
  id: number
  mediaType: MediaType
  title: string
  posterUrl: string | null
  stage: RequestStage
  requestedAt: string
  availableAt: string | null
  releaseDate?: string | null
  /** True when the release date is still in the future — nothing to grab yet. */
  awaitingRelease?: boolean
  /** Season numbers for a per-season TV request; null for films. */
  seasons?: number[] | null
  /** "S2" / "S1–S3", ready to render beside the title. */
  seasonLabel?: string | null
  download: DownloadDetail | null
  /** True when served from the D1 snapshot because the home server is down. */
  stale: boolean
}

export type RequestFilter = 'all' | 'pending' | 'downloading' | 'available'

/**
 * Live torrent state from qBittorrent, matched to an *arr queue item by
 * infohash. Absent when qBittorrent is unreachable or the item is not a
 * torrent. `trackerHost` is a hostname only — the announce URL carries a
 * private tracker passkey and is stripped server-side.
 */
export interface TorrentState {
  hash: string
  state: string
  dlspeed: number
  upspeed: number
  ratio: number
  category: string
  numSeeds: number
  numLeechs: number
  numComplete: number
  numIncomplete: number
  availability: number
  downloaded: number
  uploaded: number
  amountLeft: number
  eta: number | null
  trackerHost: string | null
  savePath: string
  addedOn: number
  timeActive: number
}

export interface DownloadsResponse extends ListResponse<DownloadItem> {
  sources?: { radarr: boolean; sonarr: boolean; qbittorrent: boolean }
  errors?: string[]
}

export interface DownloadItem {
  id: string
  title: string
  /** Which queue the item came from. */
  service?: string
  mediaType?: MediaType | string
  status: string
  progress: number
  size: number
  sizeLeft: number
  eta: number | null
  client: string | null
  errorMessage?: string | null
  username?: string | null
  torrent?: TorrentState | null
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export interface AppNotification {
  id: string
  event: NotificationEvent
  title: string
  body: string | null
  url: string | null
  imageUrl?: string | null
  readAt: string | null
  createdAt: string
}

export interface NotificationPreference {
  event: NotificationEvent
  enabled: boolean
}

export interface NotificationPreferencesResponse {
  preferences: NotificationPreference[]
}

export interface VapidKeyResponse {
  key: string
}

// ---------------------------------------------------------------------------
// Invites, profiles, admin
// ---------------------------------------------------------------------------

export interface InvitePreview {
  valid: boolean
  reason?: string
  serverName: string
  profileName?: string | null
  expiresAt?: string | null
}

export interface RedeemResponse {
  user: User
}

export type InviteKind = 'invite' | 'referral'

export interface Invite {
  id: string
  code: string
  kind: InviteKind
  label: string | null
  profileId: string | null
  profileName?: string | null
  ownerUserId?: string | null
  createdByUserId?: string | null
  expiresAt: string | null
  maxUses: number | null
  usedCount: number
  accountExpiryDays: number | null
  revokedAt: string | null
  createdAt: string
}

export interface Profile {
  id: string
  name: string
  description: string | null
  isDefault: boolean
  policy?: Record<string, unknown> | null
  configuration?: Record<string, unknown> | null
  libraryIds: string[] | null
  accountExpiryDays: number | null
  seerrMovieQuotaLimit?: number | null
  seerrMovieQuotaDays?: number | null
  seerrTvQuotaLimit?: number | null
  seerrTvQuotaDays?: number | null
  createdAt?: string
  updatedAt?: string
  /** Convenience count the admin table shows when present. */
  userCount?: number
}

export interface ProfileInput {
  name: string
  description?: string | null
  isDefault?: boolean
  libraryIds?: string[] | null
  accountExpiryDays?: number | null
  seerrMovieQuotaLimit?: number | null
  seerrMovieQuotaDays?: number | null
  seerrTvQuotaLimit?: number | null
  seerrTvQuotaDays?: number | null
}

export interface JellyfinLibrary {
  id: string
  name: string
  type?: string | null
  collectionType?: string | null
}

export interface AdminUser extends User {
  profileName?: string | null
  /** Onboarding state — who actually finished setting up. */
  lastPlatform?: string | null
  pwaInstalled?: boolean
  pwaInstalledAt?: string | null
  pushPermission?: string | null
  clientSeenAt?: string | null
  pushDevices?: number
  /** Present when the Jellyfin side of the merge succeeded. */
  jellyfin?: {
    lastActivityDate?: string | null
    isDisabled?: boolean
  } | null
}

export interface AdminUserPatch {
  label?: string | null
  notes?: string | null
  expiresAt?: string | null
  disabled?: boolean
  profileId?: string | null
}

export interface InviteInput {
  label?: string | null
  profileId?: string | null
  expiresAt?: string | null
  maxUses?: number | null
  accountExpiryDays?: number | null
}

export interface ActivityEntry {
  id: string
  type: string
  message: string
  actorUserId: string | null
  actorUsername?: string | null
  subjectUserId: string | null
  subjectUsername?: string | null
  data?: Record<string, unknown> | null
  ip?: string | null
  createdAt: string
}

export interface AnnounceInput {
  title: string
  body: string
  url?: string | null
  userIds?: string[] | null
}

export interface AnnounceResponse {
  notified?: number
  pushed?: number
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export interface ServiceHealth {
  service: string
  ok: boolean
  checkedAt: string | null
  latencyMs: number | null
  message?: string | null
}
